'use strict';
import { NativeModules, Platform, AppState } from 'react-native';
import packageInfo from '../package.json';
import Constants from './constants';
import NetworkLogger from './NetworkLogger';
const { RNSecLib, SettingsManager } = NativeModules;
const { EnvironmentTypes, EventTypes, UIElements } = Constants;

class SecLib {
	static launchTimeCalculated = false;
	static currentAppState = 'foreground';
	static appLaunchTime = null;
	static isInBG = false;
	static logForegroundEventFromWTP = false;
	static subComponents = [];
	static localObject = null;
	static currentLocale = null;

	static currentConfig = {
		isSmapiOn: true,
		environment: EnvironmentTypes.GCP_TEST,
		isExceptionTrackingOn: true,
		isNetworkTrackingOn: false,
		isVerbose: false,
		traceIdKey: 'vf-trace-transaction-id',
		userId: null,
		packageName: null,
	};
	static navState = {
		type: null,
		currentRouteName: null,
		prevRoutName: null,
	};
	static stateDict = {
		active: 'foreground',
		inactive: 'background',
		background: 'background',
		foreground: 'foreground',
	};

	static init({
		environment = SecLib.currentConfig.environment,
		isExceptionTrackingOn = SecLib.currentConfig.isExceptionTrackingOn,
		isNetworkTrackingOn = SecLib.currentConfig.isNetworkTrackingOn,
		isVerbose = SecLib.currentConfig.isVerbose,
		traceIdKey = SecLib.currentConfig.traceIdKey,
		packageName = SecLib.currentConfig.packageName,
	}) {
		RNSecLib.setup(environment, isExceptionTrackingOn, isVerbose, packageName);
		if (Platform.OS === 'ios') {
			SecLib._setJSExceptionHandling(isExceptionTrackingOn);
		}
		RNSecLib.configureNetworkMonitoring(isNetworkTrackingOn);
		RNSecLib.setTraceIdKey(traceIdKey);
		SecLib.currentConfig = {
			...SecLib.currentConfig,
			environment,
			isExceptionTrackingOn,
			isNetworkTrackingOn,
			isVerbose,
			traceIdKey,
		};
		if (Platform.OS === 'ios' && !this.currentLocale) {
			const localeAndZone = SettingsManager.settings.AppleLocale || SettingsManager.settings.AppleLanguages?.shift(); //iOS 13
			const locale = localeAndZone.split(/-|_/).shift();
			this.setLocale(locale);
		}
		AppState.addEventListener('change', SecLib._handleAppStateChangeForLogEvent);
	}

	static setLocale(locale) {
		if (!locale) {
			return;
		}
		RNSecLib?.setLocale(locale);
		this.currentLocale = locale;
		if (Platform.OS === 'ios' && this.localObject && this.localObject[locale]) {
			this._setIosLocale(this.localObject[locale]);
		}
	}

	static _setIosLocale(localeObject) {
		for (const key in localeObject) {
			RNSecLib?.setLocaleText(key, localeObject[key]);
		}
	}

	static setLocaleText(localesObject) {
		this.localObject = localesObject;
		if (Platform.OS === 'android') {
			for (const locale in localesObject) {
				RNSecLib?.setLocaleText(locale, localesObject[locale]);
			}
			return;
		}
		if (this.currentLocale) {
			const currentLocaleObject = localesObject[this.currentLocale];
			this._setIosLocale(currentLocaleObject);
		}
	}

	static throttleLocalEndPoint(array) {
		RNSecLib.throttleLocalEndPoint?.(array);
	}

	static setNetworkMonitoring(flag) {
		if (!SecLib.currentConfig.isSmapiOn) {
			return;
		}
		RNSecLib.configureNetworkMonitoring(flag);
		SecLib.currentConfig.isNetworkTrackingOn = flag;
	}

	static turnSMAPIOnOff(flag) {
		if (typeof flag !== 'boolean') {
			return;
		}
		RNSecLib.turnSMAPIOnOff(flag);
		SecLib.currentConfig.isSmapiOn = flag;
	}

	static async getSMAPIStatus() {
		return await RNSecLib.getSMAPIStatus();
	}
	static async setUserId(userId) {
		const shaUserId = await RNSecLib.setUserId(userId);
		SecLib.currentConfig.userId = shaUserId;
		return shaUserId;
	}

	static networkLogger(url, method, requestHeaders) {
		return new NetworkLogger(url, method, requestHeaders);
	}

	static async logComponents(components) {
		return await RNSecLib.logComponents(components);
	}

	static logEvent(
		eventType,
		eventElement,
		eventDescription,
		onPage = SecLib.navState.currentRouteName,
		onSubpage = 'NA',
		withCustomJSON = {},
	) {
		RNSecLib.logEvent(eventType, eventElement, eventDescription, onPage, onSubpage, withCustomJSON);
	}

	static logCustomEvent(
		eventElement,
		eventDescription,
		onPage = SecLib.navState.currentRouteName,
		withCustomJSON = {},
	) {
		RNSecLib.logCustomEvent(eventElement, eventDescription ?? 'NA', onPage, 'NA', withCustomJSON);
	}

	static logExceptionEvent(error) {
		RNSecLib.logExceptionEvent(error.name, error.toString());
	}

	static _logLoadTime = (id, loadTime) => SecLib.logEvent(EventTypes.page, 'Load Time', `${loadTime} sec`, id);
	static _logLoaded = (id, sub_id = 'NA') => SecLib.logEvent(EventTypes.page, 'NA', 'Loaded', id, sub_id);
	static _logUnloaded = id => SecLib.logEvent(EventTypes.page, 'NA', 'UnLoaded', id);
	static _logStayTime = (id, startTime) =>
		SecLib.logEvent(EventTypes.page, 'Stay Time', `${(Date.now() - startTime) / 1000} sec`, id);

	static async appendSession(url) {
		try {
			return await RNSecLib.appendSession(url);
		} catch (error) {
			console.log(error);
		}
	}

	static startTimer(eventElement, uniqueCode) {
		RNSecLib.startTimer(eventElement, uniqueCode, Date.now());
	}

	static stopTimer(uniqueCode) {
		RNSecLib.stopTimer(uniqueCode, Date.now());
	}

	static setTraceIdKey(newValue) {
		RNSecLib.setTraceIdKey(newValue);
		SecLib.currentConfig.traceIdKey = newValue;
	}

	static setPageName(pageName) {
		RNSecLib.setPageName(pageName);
		SecLib.navState.currentRouteName = pageName;
	}

	static appDidLaunch(pageName) {
		if (SecLib.launchTimeCalculated) {
			return;
		}
		SecLib.setPageName(pageName);
		const currentEpochFixed = Date.now();
		SecLib.launchTimeCalculated = true;
		RNSecLib.appDidLaunch(currentEpochFixed);
	}

	static getConfig() {
		return { ...SecLib.currentConfig };
	}

	static trackEvent = trackingData => {
		var { action, element, id } = trackingData;
		var eventElement = `${element} - ${id}`;
		var eventType = '';
		var eventDesc = '';
		const actionList = UIElements[element];
		if (actionList && actionList.includes(action)) {
			//Following table
			switch (element) {
				/* ------ checking for UISwipe ------ */
				case 'RefreshControl': {
					eventType = EventTypes.swipe;
					eventDesc = 'Swiped page for refresh';
					break;
				}
				/* ------ checking for UIChange ------ */
				case 'TabView': {
					eventType = EventTypes.UIChange;
					eventElement = element;
					eventDesc = `Selected Page ${id}`;
					break;
				}
				/* ------ checking for UIControl ------ */
				default: {
					eventType = EventTypes.controls;
					eventDesc = action;
					break;
				}
			}
		} else {
			/* ------ checking for UICustom ------ */
			eventType = EventTypes.custom;
			eventDesc = action;
		}

		/* ---- checking if logCustomEvent or logEvent -----*/
		if (eventType === EventTypes.custom) {
			SecLib.logCustomEvent(eventElement, eventDesc);
		} else {
			SecLib.logEvent(eventType, eventElement, eventDesc);
		}
	};

	static version() {
		return packageInfo.version;
	}

	static _handleAppStateChangeForLogEvent = nextAppState => {
		if (Platform.OS !== 'android') {
			return;
		}
		const nextAppStateCpy = SecLib.stateDict[nextAppState];
		if (nextAppStateCpy !== SecLib.currentAppState && SecLib.currentConfig.isSmapiOn) {
			if (nextAppStateCpy === 'foreground') {
				SecLib.navState.prevRoutName = null;
				if (!SecLib.logForegroundEventFromWTP) {
					SecLib.logForegroundEvent();
				}
			} else {
				SecLib.isInBG = true;
			}
			SecLib.currentAppState = nextAppStateCpy;
		}
	};

	/******************** The following methods are used internally only *********************/
	static _getActiveRouteInfo(navigationState) {
		if (!navigationState) {
			return null;
		}
		const route = navigationState.routes[navigationState.index];
		// dive into nested navigators
		if (route.routes) {
			return SecLib._getActiveRouteInfo(route);
		}

		return {
			routeName: route.routeName,
			isTransitioning: navigationState.isTransitioning,
		};
	}

	static _getNavigationType(action) {
		return action.type.split('/')[1];
	}

	static _setJSExceptionHandling(flag) {
		SecLib._originalHandler =
			(ErrorUtils.getGlobalHandler && ErrorUtils.getGlobalHandler()) || ErrorUtils._globalHandler;
		if (flag) {
			ErrorUtils.setGlobalHandler(async (error, isFatal) => {
				if (Object.prototype.toString.call(error) !== '[object Error]') {
					return SecLib._originalHandler(error, isFatal);
				}
				SecLib.logExceptionEvent(error);
				return new Promise((resolve, _) => {
					setTimeout(() => {
						resolve(SecLib._originalHandler(error, isFatal));
					}, 200);
				});
			});
		}
	}

	static logForegroundEvent() {
		RNSecLib.logForegroundEvent(SecLib.navState.currentRouteName);
		SecLib.isInBG = false;
		SecLib.logForegroundEventFromWTP = false;
	}

	static checkForeground(pageId) {
		if (SecLib.logForegroundEventFromWTP) {
			SecLib.setPageName(pageId);
			SecLib.logForegroundEvent();
		}
	}

	static checkBackground() {
		if (SecLib.isInBG) {
			SecLib.logForegroundEventFromWTP = true;
		}
	}

	static logSubComponents() {
		SecLib.subComponents.forEach(componentId => SecLib._logLoaded(SecLib.navState.currentRouteName, componentId));
		SecLib.subComponents = [];
	}

	static _getListenerName(props, listenerName) {
		return props.route ? listenerName.toLowerCase() : `did${listenerName}`;
	}

	/********* The following methods are used for debugging purposes only, do not use unless necessary *********/
	static async getAppLaunchTime() {
		const appLaunchTime = SecLib.appLaunchTime || (await RNSecLib.getAppLaunchTime());
		return Math.trunc(appLaunchTime * 100) / 100 + ' secs';
	}

	static dummy1k() {
		RNSecLib.dummy1k();
	}

	static invokeNC() {
		RNSecLib.invokeNC();
	}
}

export default SecLib;
